import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiAttachmentFile, PapiDeviation } from '@wix/da-papi-types';
import { toMediaSrcFile } from '@wix/da-shared-react/pkg/utils/DeviationMediaService/helpers';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import CloseIcon from '@wix/da-ds/pkg/Icons/24x24/Close';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import {
  BiData,
  SubmitDeleteMultiImageFileClickBiEvent,
} from '@wix/da-bi/pkg/events';
import PreviewThumb, { Props as PreviewThumbProps } from '../PreviewThumb';

import s from './AttachmentThumb.scss';

interface Props extends PreviewThumbProps {
  deviation?: PapiDeviation;
  attachment: PapiAttachmentFile;
  removeAttachment?: (fileId: number, callback?: () => void) => void;
}

const AttachmentThumb: React.FC<Props> = ({
  attachment,
  deviation,
  selected,
  removeAttachment,
  ...props
}) => {
  const { t } = useTranslation();
  const [isRemoved, setIsRemoved] = useState(false);
  const selectedMediaType = attachment.media!.types![0];
  const deviationAttachmentIds = (
    deviation?.extended?.additionalMedia || []
  ).map(att => att.fileId);
  const onRemove = useCallback(
    e => {
      e.stopPropagation();
      if (attachment && attachment.fileId) {
        if (deviation) {
          removeAttachment?.(attachment.fileId, () => setIsRemoved(true));
        }
      }
    },
    [removeAttachment, deviation, attachment]
  );
  const thumbData = toMediaSrcFile(attachment, selectedMediaType);

  if (!thumbData) {
    return null;
  }
  return (
    <PreviewThumb
      {...props}
      selected={selected}
      className={classnames(
        s['root'],
        isRemoved && s['removed'],
        selected && s['selected']
      )}
    >
      {deviation && (
        <IconButton
          className={s['btn-remove']}
          onClick={onRemove}
          size="small"
          icon={CloseIcon}
          aria-label={t('common.remove')}
          biData={
            deviation &&
            BiData<SubmitDeleteMultiImageFileClickBiEvent>({
              evid: 35,
              typeid: getDeviationResourceType(deviation),
              itemid: deviation.deviationId,
              sectionname: 'attachment_slider',
              rank: deviationAttachmentIds.indexOf(attachment.fileId!),
            })
          }
        />
      )}
      <img
        src={thumbData.src}
        alt={attachment.filename}
        width={56}
        height={56}
        style={{ objectFit: 'cover' }}
      />
    </PreviewThumb>
  );
};

AttachmentThumb.displayName = 'AttachmentThumb';

export default AttachmentThumb;
